/**
 * SettingsModal
 *
 * The account settings dialog, opened from "Settings" in the account menu.
 *
 *   Account        — name, username, email, password, sessions, delete account
 *   Preferences    — timezone, defaults, and how JAI advises this user
 *   Connections    — which fantasy platforms JAI holds credentials for
 *   Subscription   — current plan, price, renewal, upgrade / manage links
 *   Cancel plan    — cancel a paid plan (only shown when there's one to cancel)
 *   Privacy & data — the agreements they accepted, and a full data export
 *
 * The sections themselves live in components/settings/ — this file owns the
 * shell: loading the profile, the tab strip, and keeping the rest of the app
 * in step when something here changes the user.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authApi, billingApi } from '../services/api'
import { PLANS, fmtPrice } from '../config/plans'
import { PLAN_CREDIT_MONTHLY_LIMIT } from '../config/planAccess'
import CreditUsageBar from './CreditUsageBar'
import TopupPacks from './TopupPacks'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import ModalPortal, { ModalHeader, GhostButton, SolidButton } from './ModalPortal'
import { Row, SectionNote, Notice, formatDate } from './settings/SettingsFields'
import AccountSection from './settings/AccountSection'
import PreferencesSection from './settings/PreferencesSection'
import ConnectionsSection from './settings/ConnectionsSection'
import PrivacySection from './settings/PrivacySection'
import { IS_NATIVE_APP } from '../services/runtime'
import { Notice as RuleNotice } from './JaiV2'

// ── Small presentational pieces ─────────────────────────────────────────────

/**
 * What the Account tab shows during a guest session.
 *
 * Every control the real Account tab offers needs something a guest doesn't
 * have: changing the email needs a password to confirm it, changing the
 * password needs the current one, and "log out everywhere" and "delete
 * account" are meaningless for a session that only exists in this browser.
 * Rather than show a screen of controls that would each fail on submit, offer
 * the one action that unlocks all of them.
 */
function GuestAccountSection({ onCreateAccount }) {
  return (
    <div>
      <Notice>
        You're using JAI as a guest. This session lives only in this browser — clearing your
        browsing data or opening JAI somewhere else will start you over with nothing.
      </Notice>
      <p style={{ margin: '14px 0 16px', fontFamily: JAI_FONTS.tight, fontSize: 13.5, lineHeight: 1.6, color: JAI_COLORS.mute }}>
        Creating a free account keeps everything you've set up so far — your leagues, notes and
        chat history all carry across — and gives you a name, email and password you can sign
        back in with from anywhere.
      </p>
      <SolidButton onClick={onCreateAccount}>Create your free account</SolidButton>
    </div>
  )
}

/**
 * Green/amber/grey dot + label describing the subscription state.
 *
 * `cancelling` takes priority over the raw Stripe status: a cancelled-but-still-
 * running subscription reports 'active' or 'trialing' right up until the period
 * ends, and showing plain "Active" there would hide the fact that it's due to
 * stop — exactly the kind of unclear disclosure the auto-renewal rules target.
 */
function StatusPill({ status, cancelling = false }) {
  const tone = cancelling
    ? { color: '#B8860B', label: 'Cancels at period end' }
    : {
        active:   { color: JAI_COLORS.success, label: 'Active' },
        trialing: { color: JAI_COLORS.success, label: 'Free trial' },
        past_due: { color: '#B8860B',          label: 'Payment overdue' },
        canceled: { color: JAI_COLORS.mute,    label: 'Cancelled' },
        none:     { color: JAI_COLORS.mute,    label: 'No subscription' },
      }[status] || { color: JAI_COLORS.mute, label: status }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone.color, flexShrink: 0 }} />
      <span style={{ fontSize: 14.5, fontWeight: 500, color: JAI_COLORS.navy }}>{tone.label}</span>
    </span>
  )
}

/**
 * The plain-English list of what cancelling actually does.
 *
 * Every line here exists for a compliance reason: the customer has to be told,
 * before they confirm, that charges stop, exactly when access ends, that they
 * keep what they paid for until then, and what they're left with afterwards.
 * Don't trim these to tidy up the layout.
 */
function CancellationFacts({ endDate, trialing }) {
  const facts = [
    trialing
      ? <>You <strong>won't be charged</strong> — cancelling during your free trial means no payment is ever taken.</>
      : <>You <strong>won't be charged again.</strong> No further payments will be taken from your card.</>,
    endDate
      ? <>You keep every paid feature until <strong>{endDate}</strong>{trialing ? ', when your trial would have ended' : ' — the end of the period you\'ve already paid for'}.</>
      : <>You keep every paid feature until the end of the period you've already paid for.</>,
    <>After that your account moves to the free <strong>Starter</strong> plan: 1 connected league, {PLAN_CREDIT_MONTHLY_LIMIT.starter} JAI credits a month, and Draft Room analysis for rounds 1–5.</>,
    <>Nothing is deleted. Your account, leagues, and notes stay exactly as they are, and you can resubscribe whenever you like.</>,
  ]

  return (
    <ul style={{ margin: '14px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11 }}>
      {facts.map((fact, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, fontSize: 13.5, lineHeight: 1.6, color: JAI_COLORS.navy }}>
          <span aria-hidden="true" style={{ color: JAI_COLORS.blue, fontWeight: 700, flexShrink: 0 }}>·</span>
          <span>{fact}</span>
        </li>
      ))}
    </ul>
  )
}

// ── Component ───────────────────────────────────────────────────────────────

export default function SettingsModal({ onClose, initialSection }) {
  const navigate = useNavigate()
  const { user, isGuest, refreshPlan, updateUser, replaceToken, logout } = useAuth()

  const [section, setSection] = useState(initialSection || 'account')
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [portalBusy, setPortalBusy] = useState(false)

  // Cancellation flow. `confirming` is the second step — cancelling is two
  // deliberate clicks: no accidental cancels, but no obstacle course either.
  const [confirming, setConfirming] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelDone, setCancelDone] = useState(null)   // { accessUntil, wasTrialing }
  const [resumeBusy, setResumeBusy] = useState(false)
  const [scheduledChange, setScheduledChange] = useState(null) // { plan, interval, effectiveAt } — a downgrade queued for renewal
  const [scheduledBusy, setScheduledBusy] = useState(false)
  const [scheduledError, setScheduledError] = useState('')

  // Load the full profile fresh rather than trusting the copy saved in the
  // browser at login — a subscription bought since then wouldn't show up.
  useEffect(() => {
    let cancelled = false
    authApi.getMe()
      .then(({ data }) => { if (!cancelled) setProfile(data.user) })
      .catch(() => { if (!cancelled) setError('Could not load your account details.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Scheduled-change info lives on the billing status endpoint, not on /auth/me,
  // so it needs its own fetch. (The credit balance is shown by CreditUsageBar,
  // which reads the same endpoint through useCredits.) Deliberately silent on
  // failure: the rest of the subscription tab still works without it.
  useEffect(() => {
    let cancelled = false
    billingApi.getStatus()
      .then(({ data }) => {
        if (cancelled) return
        setScheduledChange(data?.scheduledChange || null)
      })
      .catch(() => { /* non-fatal — the row just doesn't render */ })
    return () => { cancelled = true }
  }, [])

  // Fall back to the cached user while the request is in flight, so the
  // dialog shows real details immediately instead of empty rows.
  const details = profile || user || {}

  /**
   * Fold a saved change back into both this dialog and the rest of the app.
   *
   * The sidebar and account menu read the name straight out of AuthContext, so
   * without the updateUser call a renamed user would keep seeing their old name
   * until their next login.
   */
  const applyUserUpdate = (fields) => {
    setProfile(p => ({ ...(p || {}), ...fields }))
    updateUser(fields)
  }

  /** The account is gone — drop the session and send them to the landing page.
   *  Deliberately /welcome rather than '/': the app front door would hand them
   *  a fresh guest account, which reads as the deletion having failed. */
  const handleDeleted = async () => {
    await logout()
    onClose()
    navigate('/welcome')
  }

  const planId = details.subscriptionPlan || 'starter'
  const plan = PLANS.find(p => p.id === planId)
  const planName = plan?.name || 'Starter'
  const status = details.subscriptionStatus || 'none'
  const subscribed = ['active', 'trialing'].includes(status)
  const trialing = status === 'trialing'

  // What they actually pay, stated rather than implied. `subscriptionInterval`
  // comes off the Stripe price via the webhook, so an annual subscriber sees
  // the real yearly total instead of a monthly rate they never get charged.
  const interval = details.subscriptionInterval
  const priceLabel = (() => {
    if (!plan || plan.free) return null
    if (interval === 'year')  return `${fmtPrice(plan.annualTotal)} billed yearly`
    if (interval === 'month') return `${fmtPrice(plan.monthly)} billed monthly`
    return null
  })()

  // A cancellation just made in this dialog wins over the loaded profile, so
  // the UI updates instantly instead of waiting for a re-fetch.
  const cancelling = cancelDone ? true : Boolean(details.subscriptionCancelAtPeriodEnd)
  const periodEnd = cancelDone?.accessUntil || details.subscriptionCurrentPeriodEnd
  const endDateLabel = periodEnd ? formatDate(periodEnd, details.timezone) : null

  // The Cancel tab only exists when there's something to cancel. Past_due is
  // included on purpose — someone whose card failed must still be able to stop
  // the subscription rather than being trapped by a billing problem. Not on
  // native: the whole cancel/resume flow is a billing control, and Guideline
  // 3.1.1 means native never shows billing controls of any kind — cancelling
  // a subscription bought on the web happens on the web too.
  const canCancel = !IS_NATIVE_APP && (subscribed || status === 'past_due')
  const sections = [
    { id: 'account', label: 'Account' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'connections', label: 'Connections' },
    { id: 'subscription', label: 'Subscription' },
    ...(canCancel ? [{ id: 'cancel', label: 'Cancel plan' }] : []),
    { id: 'privacy', label: 'Privacy & data' },
  ]

  const goToPricing = () => {
    onClose()
    navigate('/pricing')
  }

  const goToSignup = () => {
    onClose()
    navigate('/signup')
  }

  // Cancel at period end. The user keeps everything they paid for until the
  // date shown; Stripe takes no further payment.
  const confirmCancel = async () => {
    setCancelBusy(true)
    setError('')
    try {
      const { data } = await billingApi.cancelSubscription()
      setCancelDone({ accessUntil: data.accessUntil, wasTrialing: data.wasTrialing })
      setConfirming(false)
      // Keep the loaded profile in step so the Subscription tab reads correctly
      // without needing a re-fetch.
      setProfile(p => (p ? {
        ...p,
        subscriptionCancelAtPeriodEnd: true,
        subscriptionStatus: data.status || p.subscriptionStatus,
        subscriptionCurrentPeriodEnd: data.accessUntil || p.subscriptionCurrentPeriodEnd,
      } : p))
      // Paid features stay on until the period ends, so the plan doesn't change
      // yet — but re-reading keeps context in step with the server.
      refreshPlan?.()
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        "We couldn't cancel your plan just now. Nothing has changed and you haven't been charged anything extra — please try again, or message us through Get help."
      )
    } finally {
      setCancelBusy(false)
    }
  }

  // Drop a downgrade that is queued for the next renewal. The plan they're on
  // doesn't change either way, so there's nothing to charge or refund.
  const cancelScheduledSwitch = async () => {
    setScheduledBusy(true)
    setScheduledError('')
    try {
      await billingApi.cancelScheduledChange()
      setScheduledChange(null)
      refreshPlan?.()
    } catch (err) {
      setScheduledError(err?.response?.data?.error || "We couldn't cancel that change. Please try again or message us through Get help.")
    } finally {
      setScheduledBusy(false)
    }
  }

  // Undo a scheduled cancellation.
  const undoCancel = async () => {
    setResumeBusy(true)
    setError('')
    try {
      const { data } = await billingApi.resumeSubscription()
      setCancelDone(null)
      // Take the status from Stripe's reply as well as the dates — someone
      // undoing a cancellation mid-trial is still 'trialing', and the loaded
      // profile could say otherwise until it's next re-fetched.
      setProfile(p => (p ? {
        ...p,
        subscriptionCancelAtPeriodEnd: false,
        subscriptionStatus: data.status || p.subscriptionStatus,
        subscriptionCurrentPeriodEnd: data.renewsOn,
      } : p))
      refreshPlan?.()
    } catch (err) {
      setError(err?.response?.data?.error || "We couldn't restart your plan. Please try again or message us through Get help.")
    } finally {
      setResumeBusy(false)
    }
  }

  // Stripe's own portal is where cards, invoices and cancellation live —
  // handing off means JAI never touches payment details itself.
  const openBillingPortal = async () => {
    setPortalBusy(true)
    setError('')
    try {
      const { data } = await billingApi.openPortal()
      window.location.href = data.url
    } catch {
      setError('Could not open the billing portal. Please try again.')
      setPortalBusy(false)
    }
  }

  return (
    <ModalPortal onClose={onClose} width={600} labelledBy="settings-title">
      <ModalHeader title="Account settings" titleId="settings-title" onClose={onClose} />

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 2, padding: '12px 16px 0', flexShrink: 0, borderBottom: `1px solid ${JAI_COLORS.hair}`, overflowX: 'auto' }}>
        {sections.map(({ id, label }) => {
          const on = section === id
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              style={{
                padding: '9px 11px 11px', border: 'none', background: 'transparent',
                fontFamily: JAI_FONTS.tight, fontSize: 13.5, fontWeight: on ? 650 : 500,
                color: on ? JAI_COLORS.navy : JAI_COLORS.mute,
                borderBottom: `2px solid ${on ? JAI_COLORS.blue : 'transparent'}`,
                marginBottom: -1, cursor: 'pointer', transition: 'color 150ms',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Section body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px 22px 22px' }}>
        {error && (
          <RuleNotice tone="error" style={{ marginTop: 16 }}>{error}</RuleNotice>
        )}

        {section === 'account' && (
          loading && !profile
            ? <SectionNote>Loading your account…</SectionNote>
            : isGuest
              ? <GuestAccountSection onCreateAccount={goToSignup} />
              : (
                <AccountSection
                  details={details}
                  onUpdated={applyUserUpdate}
                  onTokenReplaced={replaceToken}
                  onDeleted={handleDeleted}
                />
              )
        )}

        {section === 'preferences' && (
          loading && !profile
            ? <SectionNote>Loading your preferences…</SectionNote>
            : <PreferencesSection details={details} onUpdated={applyUserUpdate} />
        )}

        {section === 'connections' && <ConnectionsSection />}

        {section === 'subscription' && (
          <div>
            <Row label="Plan" value={planName} />
            <Row label="Status" value={<StatusPill status={status} cancelling={cancelling} />} />
            {/* Price and the renewal/access-until date are billing details,
                not just plan facts — Guideline 3.1.1 keeps them off native
                entirely, even read-only. "Pro — active" is fine to state;
                "$14.99/month, renews 12 Sept" reads as a place to transact. */}
            {!IS_NATIVE_APP && subscribed && priceLabel && (
              <Row label="Price" value={priceLabel} />
            )}
            {!IS_NATIVE_APP && subscribed && (
              <Row
                // The label carries the meaning here. Saying "Renews" next to a
                // date on a plan that is in fact ending would be misleading, so
                // a pending cancellation flips this to "Access until".
                label={cancelling ? 'Access until' : (trialing ? 'Trial ends' : 'Renews')}
                value={endDateLabel || '—'}
              />
            )}

            {/* How much of their JAI-credit allowance is gone. The point is that
              * a user never hits a wall they couldn't see coming, so this is
              * shown for every plan, including Starter, which has the tightest
              * allowance. Numbers come from the API (via useCredits), never
              * from the local plan constants, so a trial's reduced allowance
              * and the server's real limit always show correctly. */}
            <Row label="JAI credits" value={<CreditUsageBar compact />} />

            {/* Top-ups are bought on the website only. In the iOS app there is
              * no purchase path and no link, just a plain statement. */}
            {IS_NATIVE_APP ? (
              <SectionNote>Manage your plan and credits on play-jai.com.</SectionNote>
            ) : (
              <div style={{ margin: '4px 0 14px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: JAI_COLORS.navy, marginBottom: 8 }}>Need more credits?</div>
                <TopupPacks />
              </div>
            )}

            {!IS_NATIVE_APP && cancelling && (
              <Notice tone="warn" title="Your plan is set to end">
                {endDateLabel
                  ? <>You've cancelled, so <strong>no further payments will be taken</strong>. You keep {planName} until <strong>{endDateLabel}</strong>, then your account moves to the free Starter plan.</>
                  : <>You've cancelled, so <strong>no further payments will be taken</strong>. You keep {planName} until the end of your current paid period, then your account moves to the free Starter plan.</>}
                <div style={{ marginTop: 12 }}>
                  <SolidButton onClick={undoCancel} loading={resumeBusy}>
                    {resumeBusy ? 'Restarting…' : 'Keep my plan'}
                  </SolidButton>
                </div>
              </Notice>
            )}

            {/* A downgrade waiting for the renewal date. The Plan row above still
                shows the CURRENT plan — it only changes when the switch really
                happens. Hidden on native with the other billing detail
                (Guideline 3.1.1). */}
            {!IS_NATIVE_APP && subscribed && scheduledChange && (
              <Notice tone="warn" title={`Your plan changes to ${PLANS.find(p => p.id === scheduledChange.plan)?.name || scheduledChange.plan} on ${formatDate(scheduledChange.effectiveAt, details.timezone)}`}>
                You keep {planName} until then, and nothing is charged or refunded today. After that you pay the {PLANS.find(p => p.id === scheduledChange.plan)?.name || scheduledChange.plan} price, billed {scheduledChange.interval === 'year' ? 'yearly' : 'monthly'}.
                <div style={{ marginTop: 12 }}>
                  <SolidButton onClick={cancelScheduledSwitch} loading={scheduledBusy}>
                    {scheduledBusy ? 'Cancelling…' : 'Cancel this change'}
                  </SolidButton>
                </div>
                {scheduledError && <div style={{ marginTop: 8 }}>{scheduledError}</div>}
              </Notice>
            )}

            {/* No "Change plan" / "Upgrade plan" / "Manage billing" on native
                at all — every one of them is a purchase or billing-management
                CTA pointed at the web (Guideline 3.1.1). Plan and status above
                are the complete native subscription view. */}
            {!IS_NATIVE_APP && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
                  <SolidButton onClick={goToPricing}>
                    {subscribed ? 'Change plan' : 'Upgrade plan'}
                  </SolidButton>
                  {(subscribed || status === 'past_due') && (
                    <GhostButton onClick={openBillingPortal} disabled={portalBusy}>
                      {portalBusy ? 'Opening…' : 'Manage billing'}
                    </GhostButton>
                  )}
                </div>

                <SectionNote>
                  {subscribed || status === 'past_due'
                    ? 'Manage billing opens Stripe, where you can update your card or download invoices.'
                    : 'You\'re on the free Starter plan. Upgrading unlocks more leagues and every JAI tool.'}
                </SectionNote>
              </>
            )}

          </div>
        )}

        {section === 'cancel' && !IS_NATIVE_APP && (
          <div>
            {/* ── Already cancelled ─────────────────────────────────────── */}
            {cancelling ? (
              <>
                <Notice tone="good" title={cancelDone?.wasTrialing || trialing ? 'Your trial is cancelled' : 'Your subscription is cancelled'}>
                  {cancelDone?.wasTrialing || trialing
                    ? <><strong>You won't be charged.</strong> {endDateLabel ? <>You keep {planName} until <strong>{endDateLabel}</strong>, then</> : 'When your trial ends'} your account moves to the free Starter plan.</>
                    : <><strong>No further payments will be taken.</strong> You keep {planName} until {endDateLabel ? <strong>{endDateLabel}</strong> : 'the end of your current paid period'}, then your account moves to the free Starter plan.</>}
                  <div style={{ marginTop: 10 }}>
                    We've emailed you a confirmation with these details for your records.
                  </div>
                </Notice>

                <SectionNote>
                  Changed your mind? Restarting before {endDateLabel || 'your plan ends'} picks
                  up exactly where you left off — nothing is interrupted and you
                  won't be charged twice.
                </SectionNote>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
                  <SolidButton onClick={undoCancel} loading={resumeBusy}>
                    {resumeBusy ? 'Restarting…' : 'Keep my plan'}
                  </SolidButton>
                  <GhostButton onClick={() => setSection('subscription')}>
                    Back to subscription
                  </GhostButton>
                </div>
              </>
            ) : (
              <>
                {/* ── Step 1: the disclosures ────────────────────────────── */}
                <div style={{ paddingTop: 18 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 650, color: JAI_COLORS.navy }}>
                    Cancel your {planName} plan
                  </div>
                  <p style={{ margin: '7px 0 0', fontSize: 13.5, lineHeight: 1.6, color: JAI_COLORS.mute }}>
                    Here's exactly what happens if you cancel — please read
                    before confirming.
                  </p>
                </div>

                <CancellationFacts endDate={endDateLabel} trialing={trialing} />

                {!trialing && (
                  <Notice tone="neutral">
                    Because you keep full access for the rest of the period you've
                    paid for, payments already made aren't refunded. This doesn't
                    affect your rights under the Australian Consumer Law or any
                    other consumer law that applies to you — if something's gone
                    wrong, message us through Get help.
                  </Notice>
                )}

                {/* ── Step 2: confirm ────────────────────────────────────── */}
                {confirming ? (
                  <Notice tone="warn" title="Confirm cancellation">
                    {trialing
                      ? <>Cancel your free trial? You won't be charged{endDateLabel ? <>, and you keep {planName} until <strong>{endDateLabel}</strong></> : ''}.</>
                      : <>Cancel {planName}? You'll keep access until <strong>{endDateLabel || 'the end of your paid period'}</strong> and won't be charged again.</>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
                      <SolidButton tone="danger" onClick={confirmCancel} loading={cancelBusy}>
                        {cancelBusy ? 'Cancelling…' : 'Yes, cancel my plan'}
                      </SolidButton>
                      <GhostButton onClick={() => setConfirming(false)} disabled={cancelBusy}>
                        Never mind
                      </GhostButton>
                    </div>
                  </Notice>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
                    <SolidButton tone="danger" onClick={() => setConfirming(true)}>
                      Cancel my plan
                    </SolidButton>
                    <GhostButton onClick={() => setSection('subscription')}>
                      Keep my plan
                    </GhostButton>
                  </div>
                )}

                <SectionNote>
                  You can cancel here at any time, in a couple of clicks — you
                  never need to call or email us to stop your subscription.
                </SectionNote>
              </>
            )}
          </div>
        )}

        {section === 'privacy' && <PrivacySection details={details} />}
      </div>
    </ModalPortal>
  )
}
